<template>
  <nav class="navbar navbar-expand px-3 py-2 border-bottom bg-white shadow-sm">
    <!-- Toggle Button -->
    <button class="btn btn-outline-secondary border-0" type="button" @click="$emit('toggle-sidebar')">
      <i class="fa-solid fa-bars"></i>
    </button>

    <!-- Right Side Menu -->
    <div class="navbar-collapse collapse">
      <ul class="navbar-nav ms-auto align-items-center">

        <!-- Language Switcher -->
        <li class="nav-item dropdown me-3">
          <a
            class="lang-btn-light"
            href="#"
            id="langDropdown"
            role="button"
            data-bs-toggle="dropdown"
            aria-expanded="false"
            :aria-label="$t('Change language')"
          >
            <i class="fa-solid fa-earth-asia lang-btn__globe"></i>
            <span>{{ activeLang.label }}</span>
            <i class="fa-solid fa-chevron-down lang-btn__caret"></i>
          </a>
          <ul class="dropdown-menu dropdown-menu-end shadow lang-menu" aria-labelledby="langDropdown">
            <li
              v-for="lang in languages"
              :key="lang.code"
            >
              <a
                class="dropdown-item lang-item"
                :class="{ active: currentLang === lang.code }"
                href="#"
                @click.prevent="changeLanguage(lang.code)"
              >
                <span class="lang-item__flag">{{ lang.flag }}</span>
                <span class="flex-grow-1 text-start">{{ lang.label }}</span>
                <i
                  v-if="currentLang === lang.code"
                  class="fa-solid fa-check lang-item__check"
                ></i>
              </a>
            </li>
          </ul>
        </li>

        <!-- เครื่องพิมพ์ของเครื่องนี้ (utils/printerSettings.js) — เปิด offcanvas ที่ AdminLayout mount ไว้
             ซ่อนบนเบราว์เซอร์ที่พิมพ์ตรงไม่ได้เลย (iPhone/iPad) — กดเข้าไปก็ตั้งอะไรไม่ได้ -->
        <li v-if="canDirectPrint" class="nav-item me-3">
          <printer-settings-button btn-class="lang-btn-light topbar-icon-btn" />
        </li>

        <!-- Fullscreen toggle (ซ่อนถ้าเบราว์เซอร์ไม่รองรับ/ไม่อนุญาต) -->
        <li v-if="fsSupported" class="nav-item me-3">
          <button
            type="button"
            class="lang-btn-light topbar-icon-btn"
            :title="isFullscreen ? $t('Exit Fullscreen') : $t('Fullscreen')"
            :aria-label="isFullscreen ? $t('Exit Fullscreen') : $t('Fullscreen')"
            @click="toggleFs"
          >
            <i class="fa-solid" :class="isFullscreen ? 'fa-compress' : 'fa-expand'"></i>
          </button>
        </li>

        <!-- User Profile -->
        <li class="nav-item dropdown border-start ps-3">
          <a
            class="nav-link dropdown-toggle d-flex align-items-center p-0"
            href="#"
            id="userDropdown"
            role="button"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            <!-- User Info Text -->
            <div class="d-flex flex-column text-end me-2 lh-1 d-none d-sm-flex">
              <span class="fw-bold text-dark" style="font-size: 0.9rem;">
                {{ user.fullname || 'Admin User' }}
              </span>
              <span class="text-muted" style="font-size: 0.75rem;">
                {{ user.role || 'Administrator' }}
              </span>
            </div>
             <!-- Avatar Icon -->
             <div class="topbar-avatar">
                <i class="fa-solid fa-user"></i>
             </div>
          </a>

          <ul class="dropdown-menu dropdown-menu-end shadow user-menu mt-2" aria-labelledby="userDropdown">
            <!-- Profile header card -->
            <li class="user-menu__header">
              <div class="user-menu__avatar">
                <i class="fa-solid fa-user"></i>
              </div>
              <div class="flex-grow-1" style="min-width: 0;">
                <div class="user-menu__name text-truncate">{{ user.fullname || 'Admin User' }}</div>
                <div class="user-menu__role text-truncate">{{ user.role || 'Administrator' }}</div>
              </div>
            </li>

            <!-- แพ็กของร้าน — ดูได้ตลอดโดยไม่รกหน้าจอ · ว่าง (ไม่มีแพ็ก / อ่าน license ไม่ได้) = ไม่โชว์ -->
            <li v-if="license.planName" class="user-menu__plan">
              <div class="user-menu__plan-row">
                <span class="user-menu__plan-label">{{ $t('_plan.Current plan') }}</span>
                <span class="user-menu__plan-name">
                  <i class="fa-solid fa-crown me-1" />{{ planLabel(license.planName) }}
                </span>
              </div>
              <div v-if="license.expiresOn" class="user-menu__plan-date">
                {{ planDateText }}
              </div>
              <a :href="plansUrl" target="_blank" rel="noopener" class="user-menu__plan-link" @click.stop>
                {{ $t('_license.See plans and prices') }}
                <i class="fa-solid fa-arrow-up-right-from-square ms-1" />
              </a>
            </li>

            <li><hr class="dropdown-divider my-2"></li>

            <!-- Edit Profile -->
            <li>
              <router-link to="/profile" class="dropdown-item user-menu__item">
                <i class="fa-solid fa-user-pen user-menu__ico"></i>
                <span>{{ $t('Edit Profile') }}</span>
              </router-link>
            </li>

            <!-- Logout -->
            <li>
              <a class="dropdown-item user-menu__item user-menu__item--danger" href="#" @click.prevent="logout">
                <i class="fa-solid fa-right-from-bracket user-menu__ico"></i>
                <span>{{ $t('Logout') }}</span>
              </a>
            </li>
          </ul>
        </li>
      </ul>
    </div>
  </nav>
</template>

<script>
import Cookies from 'js-cookie';
import { setLocale } from '@/plugins/i18n';
import { Auth } from '@/services/endpoint/auth-api.service';
import { useAuthStore } from '@/stores/auth.store';
import { useLicenseStore, SUPPORT_CONTACT } from '@/stores/license.store';
import { planLabel, formatLicenseDate } from '@/utils/licenseDisplay';
import { isFullscreen, isFullscreenSupported, toggleFullscreen, onFullscreenChange } from '@/utils/fullscreen';
import PrinterSettingsButton from '@/components/printer-settings/PrinterSettingsButton.vue';
import { canDirectPrint } from '@/utils/printerSettings';

export default {
  name: "TopMenu",
  components: { PrinterSettingsButton },
  emits: ['toggle-sidebar'],
  data() {
    return {
      user: useAuthStore().user || { fullname: 'Guest', role: 'Viewer' },
      canDirectPrint: canDirectPrint(),
      fsSupported: isFullscreenSupported(),
      isFullscreen: isFullscreen(),
      removeFsListener: null,
      currentLang: this.$i18n.locale || 'th',
      // เพิ่มภาษาในอนาคต = เติม object ที่นี่ (code ต้องตรงกับ $i18n.locale)
      languages: [
        { code: 'th', label: 'ไทย', flag: '🇹🇭' },
        { code: 'en', label: 'English', flag: '🇬🇧' },
      ]
    }
  },
  computed: {
    license() {
      return useLicenseStore();
    },
    plansUrl() {
      return SUPPORT_CONTACT.plansUrl;
    },
    // หมดแล้ว (ผ่อนผัน/ล็อก) = "หมดอายุเมื่อ" · ยังไม่หมด = "ใช้ได้ถึงวันที่"
    planDateText() {
      const date = formatLicenseDate(this.license.expiresOn);
      return this.license.isExpired || this.license.isLocked
        ? this.$t('_license.Expired on') + ' ' + date
        : this.$t('_license.Valid until {date}', { date });
    },
    activeLang() {
      return this.languages.find(l => l.code === this.currentLang) || this.languages[0];
    }
  },
  mounted() {
    // sync ไอคอน fullscreen เมื่อผู้ใช้กด Esc / F11 (ไม่ได้กดผ่านปุ่มเรา)
    this.removeFsListener = onFullscreenChange(() => {
      this.isFullscreen = isFullscreen();
    });
  },
  beforeUnmount() {
    if (this.removeFsListener) this.removeFsListener();
  },
  methods: {
    planLabel,
    async toggleFs() {
      try {
        await toggleFullscreen();
      } catch (e) {
        // บางเบราว์เซอร์บล็อก fullscreen — เงียบไว้ ไม่ต้องรบกวนผู้ใช้
      }
      this.isFullscreen = isFullscreen();
    },
    changeLanguage(lang) {
      // setLocale จัดการครบ: cookie + i18n + header Locale ของ axios แล้ว reload
      // (ชื่อสินค้า/หมวด/ตัวเลือกมาจาก API จึงต้องโหลดใหม่ถึงจะเปลี่ยนภาษาตาม)
      this.currentLang = lang;
      setLocale(lang);
    },
    async logout() {
      try {
        await Auth.logout();
      } catch (e) {
      } finally {
        Cookies.remove('token');
        useAuthStore().logout();
        location.reload();
      }
    }
  }
}
</script>
